import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import "../components/PatientList.css";
import { ClipboardList, PlusCircle, Pencil, Eye, Trash2, Search, Home } from "lucide-react";
import { hasPermission, clearPermissionCache } from "../utils/permissions";
import jsPDF from "jspdf";

const PatientList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canExportExcel, setCanExportExcel] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    const loadPerms = async () => {
      try {
        setPermsLoading(true);

        // Clear any cached permissions before checking (forces fresh fetch from API)
        clearPermissionCache();

        // Force refresh permissions from API (don't use cache)
        // This ensures we get the latest permissions after they're updated
        const { getUserPermissions: getPerms } = await import("../utils/permissions");
        const freshPerms = await getPerms(true); // forceRefresh = true
        console.log('Fresh permissions from API:', freshPerms);

        const createPerm = await hasPermission('operation', 'addpatiententry');
        const editPerm = await hasPermission('operation', 'editpatiententry');

        // View permission: Check 'operation.patient' (from EditUserPage structure)
        // Allow fallback to 'registered_patient' for backward compatibility
        const hasPatient = await hasPermission('operation', 'patient');
        const hasRegisteredPatient = await hasPermission('operation', 'registered_patient');
        const viewPerm = hasPatient || hasRegisteredPatient;

        // Delete permission: Check 'operation.deletepatient' (from EditUserPage structure)
        // Allow fallback to 'deletepatiententry' for backward compatibility
        const hasDeletePatient = await hasPermission('operation', 'deletepatient');
        const hasDeletePatientEntry = await hasPermission('operation', 'deletepatiententry');
        const deletePerm = hasDeletePatient || hasDeletePatientEntry;

        // Excel export permission: Check 'excel.excelexport'
        const excelExportPerm = await hasPermission('excel', 'excelexport');

        console.log('=== PatientList Permission Check ===');
        console.log('Permissions loaded:', {
          create: createPerm,
          edit: editPerm,
          view: viewPerm,
          delete: deletePerm,
          excelExport: excelExportPerm,
          hasPatient,
          hasRegisteredPatient,
          hasDeletePatient,
          hasDeletePatientEntry
        });

        // Force state update with exact boolean values (no truthy/falsy issues)
        setCanCreate(!!createPerm);
        setCanEdit(!!editPerm);
        setCanView(!!viewPerm);
        setCanDelete(!!deletePerm);
        setCanExportExcel(!!excelExportPerm);

        // Log final state for debugging
        console.log('Permission states set:', {
          canCreate: !!createPerm,
          canEdit: !!editPerm,
          canView: !!viewPerm,
          canDelete: !!deletePerm,
          canExportExcel: !!excelExportPerm
        });
      } catch (error) {
        console.error('Error loading permissions:', error);
        // On error, deny all permissions explicitly
        setCanCreate(false);
        setCanEdit(false);
        setCanView(false);
        setCanDelete(false);
        setCanExportExcel(false);
      } finally {
        setPermsLoading(false);
        console.log('Permission loading complete. Buttons will now render based on permissions.');
      }
    };
    loadPerms();
  }, []);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Fetch patients
  const handleAssign = (entry) => {
    navigate(`/assign/${entry.patient_id}`, { state: { patient: entry } });
  };

  // const fetchPatients = async (page = 1, perPage = 10, search = "") => {
  //   setLoading(true);
  //   try {
  //     const token = (localStorage.getItem('session_token') || '').trim();
  //     const url = new URL("https://newerp.awasthivitality.com/api/patients");
  //     url.searchParams.append("page", page);
  //     url.searchParams.append("per_page", perPage);
  //     if (search) url.searchParams.append("search", search);
  //     if (token) url.searchParams.append("token", token);

  //     const response = await fetch(url, {
  //       headers: {
  //         ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  //         ...(token ? { 'X-Session-Token': token } : {}),
  //       }
  //     });
  //     const result = await response.json();

  //     if (response.status === 403) {
  //       // Permission denied
  //       setData([]);
  //       setTotalItems(0);
  //       setTotalPages(0);
  //       console.error("Permission denied:", result.message);
  //       return;
  //     }

  //     if (response.status === 401) {
  //       // Unauthorized - token invalid/missing
  //       setData([]);
  //       setTotalItems(0);
  //       setTotalPages(0);
  //       console.error("Unauthorized:", result.message);
  //       return;
  //     }

  //     if (result.success) {
  //       setData(result.data || []);
  //       setTotalItems(result.total || 0);
  //       setTotalPages(result.last_page || 1);
  //       setCurrentPage(result.current_page || 1);
  //     } else {
  //       setData([]);
  //       setTotalItems(0);
  //       setTotalPages(0);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching patient data:", error);
  //     setData([]);
  //     setTotalItems(0);
  //     setTotalPages(0);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchAllPatients = async () => {
    setLoading(true);
    try {
      const token = (localStorage.getItem("session_token") || "").trim();

      const url = new URL("https://newerp.awasthivitality.com/api/patients");
      url.searchParams.append("per_page", 30000); // get all

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Session-Token": token,
        },
      });

      const result = await response.json();

      if (result.success) {
        setAllData(result.data || []);
        setFilteredData(result.data || []);
        setTotalItems(result.data.length || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllPatients();
  }, []);
  useEffect(() => {
    let temp = [...allData];

    // Search
    if (searchQuery) {
      temp = temp.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // State
    if (selectedState) {
      temp = temp.filter(item => item.state === selectedState);
    }

    // Date
    if (selectedDate) {
      temp = temp.filter(item => item.patient_date === selectedDate);
    }

    setFilteredData(temp);
    setTotalItems(temp.length);
    setCurrentPage(1);
  }, [searchQuery, selectedState, selectedDate, allData]);
  const handleExportToExcel = async () => {
    if (!canExportExcel) {
      alert("You do not have permission to export data to Excel.");
      return;
    }

    try {
      const token = (localStorage.getItem("session_token") || "").trim();
      if (!token) {
        alert("Session expired. Please login again.");
        return;
      }

      const url = new URL("https://newerp.awasthivitality.com/api/patients");
      url.searchParams.append("per_page", 100000);
      url.searchParams.append("token", token); // ✅ IMPORTANT

      const response = await fetch(url, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "X-Session-Token": token, // ✅ REQUIRED
        },
      });

      const result = await response.json();

      if (!result.success || !Array.isArray(result.data) || result.data.length === 0) {
        alert("No data available to export.");
        return;
      }

      const exportData = result.data.map((entry) => ({
        "Patient No.": entry.pr_no,
        "Date": entry.patient_date,
        "Patient Name": entry.name,
        "Mobile No.": entry.mobile,
        "Age": entry.age,
        "City": entry.city,
        "State": entry.state,
        "First Diagnosis": entry.diagnosis_name || "-",
        "Second Diagnosis": entry.diagnosis_name1 || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Patients");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `Patient_List_${new Date().toISOString().slice(0, 10)}.xlsx`);

    } catch (error) {
      console.error("Excel Export Error:", error);
      alert("Failed to export Excel file.");
    }
  };

  const copyPhoneNumber = (number, key) => {
    if (!number) return;

    navigator.clipboard.writeText(number).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    }).catch(err => {
      console.error("Copy failed:", err);
    });
  };

  const maskPhoneNumber = (number) => {
    if (!number) return "—";

    const visible = number.slice(0, -5);
    return (
      <span
        className="masked-phone"
        title="Click to copy"
        onClick={() => copyPhoneNumber(number)}
        style={{ cursor: "pointer", color: "#007bff" }}
      >
        {visible + "*****"}
      </span>
    );
  };

  // useEffect(() => {
  //   const delayedSearch = setTimeout(() => {
  //     setCurrentPage(1);
  //     fetchPatients(1, itemsPerPage, searchQuery);
  //   }, 500);
  //   return () => clearTimeout(delayedSearch);
  // }, [searchQuery, itemsPerPage]);

  // Delete patient
  const handleDelete = async (patientId) => {
    if (!canDelete) {
      alert('You do not have permission to delete patients.');
      return;
    }
    const confirmDelete = window.confirm("Are you sure you want to delete this patient?");
    if (!confirmDelete) return;

    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      if (!token) {
        alert("Unauthorized: Please log in again.");
        return;
      }

      const url = `https://newerp.awasthivitality.com/api/patient/delete/${patientId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`,
          'X-Session-Token': token,
        },
      });

      const result = await response.json().catch(() => ({
        success: false,
        message: 'Invalid response from server'
      }));

      if (response.status === 403) {
        alert(result.message || result.msg || "You do not have permission to delete patients.");
        return;
      }

      if (response.status === 401) {
        alert(result.message || result.msg || "Unauthorized: Please log in again.");
        return;
      }

      if (response.status === 404) {
        alert(result.message || result.msg || "Patient not found.");
        return;
      }

      if (response.status === 500 || !response.ok) {
        alert(result.message || result.msg || "Server error. Please try again.");
        return;
      }

      if (result.success) {
        alert(result.message || result.msg || "Patient deleted successfully");
        // Refresh the list
        // await fetchPatients(currentPage, itemsPerPage, searchQuery);
        await fetchAllPatients();
      } else {
        alert(result.message || result.msg || "Failed to delete patient");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Network error. Please check your connection and try again.");
    }
  };
const handleGenerateCertificate = (entry) => {
  const doc = new jsPDF();

  const patientName = entry.name || "-";
  const patientCode = entry.pr_no || "-";
  const patientAge = entry.age || "-";
  const patientCity = entry.city || "-";
  const patientState = entry.state || "-";
  const diagnosis = entry.diagnosis_name || "-";
  const today = entry.patient_date || "-";

  const contentX = 20;
  const contentMaxWidth = 170;

  // ---------- HEADING ----------
  const heading = "To whom it may concern";
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(18);

  const pageWidth = doc.internal.pageSize.getWidth();
  const headingX = (pageWidth - doc.getTextWidth(heading)) / 2;

  const topMargin = 75;
  doc.text(heading, headingX, topMargin);

  let y = topMargin + 20;

  // ---------- PARAGRAPH 1 ----------
  doc.setFontSize(12);
  doc.setFont("Helvetica", "normal");
  doc.setTextColor(0, 0, 0);

  const para1 = `This is to certify that ${patientName} (${patientCode}) A/S ${patientAge}, Resident of ${patientCity} (${patientState}) is under our treatment with Poly herbal Medicine, for ${diagnosis} during the course of treatment i.e 8-10 months, they are not allowed to do any type of physical activity i.e. Running, Jumping, Zumba, Aerobics, Dancing.`;

  doc.text(para1, contentX, y, {
    maxWidth: contentMaxWidth,
    align: "justify",
  });

  y +=
    doc.getTextDimensions(para1, {
      maxWidth: contentMaxWidth,
    }).h + 10;

  // ---------- PARAGRAPH 2 ----------
  const para2 = `And during the course of treatment, they have to perform some very basic exercises which can be done at home without any help from others, and those are mandatory for the treatment as well, and the result of treatment will also increase if the patient will take sufficient bed rest.`;

  doc.text(para2, contentX, y, {
    maxWidth: contentMaxWidth,
    align: "justify",
  });

  y +=
    doc.getTextDimensions(para2, {
      maxWidth: contentMaxWidth,
    }).h + 15;

  // ---------- DATE ----------
  doc.text(`Dt : `, contentX, y);

  // ---------- FOOTER ----------
  y += 15;
  doc.text("Dr Abhishek Awasthi", contentX, y);

  y += 7;
  doc.text("Raipur, Chhattisgarh", contentX, y);

  // ---------- SAVE ----------
  doc.save(`Certificate_${patientName}.pdf`);
};


  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;

  const currentData = filteredData.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
  }, [filteredData]);

  // Pagination handler
  // const handlePageChange = (pageNumber) => {
  //   if (pageNumber >= 1 && pageNumber <= totalPages) {
  //     setCurrentPage(pageNumber);
  //     fetchPatients(pageNumber, itemsPerPage, searchQuery);
  //   }
  // };
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  // Render pagination controls
  const renderPaginationControls = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${i === currentPage ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button className="pagination-btn" onClick={() => handlePageChange(1)}>
              1
            </button>
            {startPage > 2 && <span className="pagination-dots">...</span>}
          </>
        )}

        {pageNumbers}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-dots">...</span>}
            <button className="pagination-btn" onClick={() => handlePageChange(totalPages)}>
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="main-card">
        <header className="header">
          <div className="header-content">
            {/* Left side: heading */}
            <h1 className="header-title">
              <ClipboardList className="icon" /> Patient List
            </h1>

            {/* Right side: search + add button */}
            <div className="header-actions">
              <div className="search-bar-container">
                <Search size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search patients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-bar"
                />
              </div>
              <div className="filter-container">
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  <option value="">All States</option>
                  {[...new Set(allData.map(d => d.state))].map((state, i) => (
                    <option key={i} value={state}>{state}</option>
                  ))}
                </select>

                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />

                <button
                  onClick={() => {
                    setSelectedState("");
                    setSelectedDate("");
                    setSearchQuery("");
                  }}
                >
                  Reset
                </button>
              </div>
              {!permsLoading && canExportExcel && (
                <button
                  className="header-button"
                  type="button"
                  onClick={handleExportToExcel}
                  title="Export to Excel"
                >
                  📤
                </button>
              )}

              {!permsLoading && canCreate && (
                <Link to="/NewEntry">
                  <button className="header-button" type="button">
                    <PlusCircle size={20} /> Add New Patient
                  </button>
                </Link>
              )}
            </div>
          </div>
        </header>


        <main className="main-content">
          <div className="submitted-data-table-container">
            {loading ? (
              <p className="loading-message">Loading patients...</p>
            ) : filteredData.length === 0 ? (
              <p className="no-data-message">
                {searchQuery
                  ? "No patients found matching your search."
                  : "No patient data found. Please add a new entry."}
              </p>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Patient No.</th>
                      <th>Date</th>
                      <th>Patient Name</th>
                      <th>Reg. Mode</th>
                      <th>Mobile No.</th>
                      <th>Age</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Branch</th>        {/* New column */}
                      <th>Assign</th>        {/* New column */}
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((entry) => (
                      <tr key={entry.patient_id}>
                        <td>{entry.pr_no}</td>
                        <td>{entry.patient_date}</td>
                        <td>{entry.name}</td>
                        <td>{entry.reg_mode}</td>
                        <td>
                          <span
                            onClick={() => copyPhoneNumber(entry.mobile, `mobile-${entry.patient_id}`)}
                            style={{ cursor: "pointer", color: "#007bff" }}
                            title="Click to copy"
                          >
                            {maskPhoneNumber(entry.mobile)}
                          </span>

                          {copiedKey === `mobile-${entry.patient_id}` && (
                            <span style={{ marginLeft: 6, color: "green", fontSize: "11px" }}>
                              Copied
                            </span>
                          )}
                        </td>

                        <td>{entry.age}</td>
                        <td>{entry.city}</td>
                        <td>{entry.state}</td>
                        <td>{entry.branch_name || "-"}</td>       {/* Show front branch name */}
                        <td>{entry.patient_assign || "-"}</td>   {/* Show assigned user */}
                        <td className="action-cell">
                          {!permsLoading && canView && (
                            <button
                              onClick={() => navigate(`/patient/details/${entry.patient_id}`)}
                              className="action-btn view"
                              title="View"
                            >
                              <Eye size={18} />
                            </button>
                          )}

                          {!permsLoading && canEdit && (
                            <button
                              onClick={() => navigate(`/patient/edit/${entry.patient_id}`)}
                              className="action-btn edit"
                              title="Edit"
                            >
                              <Pencil size={18} />
                            </button>
                          )}

                          <button
                            className="action-btn assign"
                            onClick={() => handleAssign(entry)}
                            title="Assign User"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                              style={{ verticalAlign: 'middle' }}
                            >
                              <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                            </svg>
                          </button>

                          <button
                            className="action-btn branch"
                            onClick={() => navigate(`/assign-branch/${entry.patient_id}`)}
                            title="Assign Branch"
                          >
                            <Home size={18} />
                          </button>
                          {/* NEW: Certificate Button */}
                          <button
                            className="action-btn cert"
                            onClick={() => handleGenerateCertificate(entry)}
                            title="Health Certificate"
                          >
                            🎓
                          </button>

                          {!permsLoading && canDelete && (
                            <button className="action-btn delete"
                              onClick={() => handleDelete(entry.patient_id)}
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}

                          {/* <button
                            className="action-btn assign"
                            onClick={() => handleAssign(entry)}   // ✅ don't pass event
                            title="Assign"
                          >
                            Assign Branch
                          </button> */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalItems > 0 && (
            <div className="table-info">
              <div>
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
              </div>
              <div>
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}

          {totalPages > 1 && renderPaginationControls()}
        </main>
      </div>
    </div>
  );
};

export default PatientList;
